// "/app/upgrade" — paid-plans / billing page.
//
// Plan comparison from /plans, current-plan badge, monthly/annual +
// BRL/USD toggles, Upgrade → Stripe Checkout, Manage Billing → Stripe
// Customer Portal. Return from Checkout lands here with ?status=...
//
// Stripe is authoritative for the actual charged amount — the toggles
// pick (cadence, currency) sent to checkout; the exact total (incl.
// annual discount / USD conversion) is confirmed on Stripe's page. We
// only show the BRL monthly price from /plans as a reference so we
// never drift from what Stripe charges.

import { useQuery } from "@tanstack/react-query";
import { clsx } from "clsx";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { PageHeader } from "@/components/ui/PageHeader";
import { usePlan } from "@/hooks/usePlan";
import { ApiError, api } from "@/lib/api";
import { useAuthStore } from "@/stores/authStore";
import { toast } from "@/stores/toastStore";
import { useDict, fmt } from "@/i18n/LocaleProvider";

type Cadence = "monthly" | "annual";
type Currency = "BRL" | "USD";

interface PriceCell {
  cents: number;
  display: string;
}
type PriceMatrix = Record<Currency, Record<Cadence, PriceCell>>;

interface PlanRow {
  slug: string;
  name: string;
  description: string;
  features: Record<string, boolean>;
  max_engagements: number;
  max_scans_per_month: number;
  max_users: number;
  price_display: string;
  prices: PriceMatrix;
  display_order: number;
}

const SELF_SERVE = new Set(["basic", "pro"]);

function defaultCurrency(): Currency {
  return navigator.language?.toLowerCase().startsWith("pt") ? "BRL" : "USD";
}

function fmtLimit(n: number, unlimited: string): string {
  return n === 0 ? unlimited : String(n);
}

// Feature flags we surface, in display order. Labels come from the dict.
const FEATURE_FLAGS = ["ai_enrichment", "deep_validation", "ai_senior_pentester", "sso"] as const;

export function UpgradePage() {
  const t = useDict().app.upgrade;
  const [params, setParams] = useSearchParams();
  const plan = usePlan();
  const refreshUser = useAuthStore((s) => s.refreshUser);

  const [cadence, setCadence] = useState<Cadence>("monthly");
  const [currency, setCurrency] = useState<Currency>(defaultCurrency);
  const [busy, setBusy] = useState<string | null>(null);

  const plans = useQuery({
    queryKey: ["plans"],
    queryFn: () => api<PlanRow[]>("/plans"),
  });

  // Return from Stripe Checkout: ?status=success|cancelled
  useEffect(() => {
    const status = params.get("status");
    if (!status) return;
    if (status === "success") {
      toast.success(t.checkoutSuccess);
      // The webhook does the real work; refresh the cached user so the
      // current-plan badge catches up once Stripe posts the event.
      void refreshUser();
    } else if (status === "cancelled") {
      toast.info(t.checkoutCancelled);
    }
    params.delete("status");
    setParams(params, { replace: true });
  }, [params, setParams, refreshUser]);

  async function startCheckout(slug: string) {
    setBusy(slug);
    try {
      const { url } = await api<{ url: string }>("/billing/checkout", {
        method: "POST",
        body: { plan_slug: slug, cadence, currency },
      });
      window.location.assign(url); // leave the SPA for Stripe's hosted page
    } catch (e) {
      const msg =
        e instanceof ApiError && typeof e.detail === "string"
          ? e.detail
          : t.checkoutError;
      toast.error(msg);
      setBusy(null);
    }
  }

  async function openPortal() {
    setBusy("__portal");
    try {
      const { url } = await api<{ url: string }>("/billing/portal", {
        method: "POST",
      });
      window.location.assign(url);
    } catch (e) {
      const msg =
        e instanceof ApiError && typeof e.detail === "string"
          ? e.detail
          : t.portalError;
      toast.error(msg);
      setBusy(null);
    }
  }

  // Auto-start checkout when arriving from a landing-page "Get Pro" click
  // (/app/upgrade?plan=pro&autostart=1). Fires once; skips if already on
  // that plan (checkout would 409).
  const autoStarted = useRef(false);
  useEffect(() => {
    if (autoStarted.current) return;
    if (params.get("autostart") !== "1") return;
    const wanted = params.get("plan") ?? "";
    if (!plans.data) return; // wait until plans load so the page is ready
    autoStarted.current = true;
    params.delete("autostart");
    params.delete("plan");
    setParams(params, { replace: true });
    if (SELF_SERVE.has(wanted) && wanted !== plan.slug) {
      void startCheckout(wanted);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, plans.data, plan.slug]);

  const onPaidPlan = SELF_SERVE.has(plan.slug);

  return (
    <div>
      <PageHeader
        title={t.title}
        subtitle={fmt(t.subtitle, { plan: plan.name })}
      />

      {/* Cadence + currency toggles */}
      <div className="flex flex-wrap items-center gap-4 mb-5">
        <Toggle<Cadence>
          label={t.billing}
          value={cadence}
          onChange={setCadence}
          options={[
            { value: "monthly", label: t.monthly },
            { value: "annual", label: t.annual },
          ]}
        />
        <Toggle<Currency>
          label={t.currency}
          value={currency}
          onChange={setCurrency}
          options={[
            { value: "BRL", label: "R$ BRL" },
            { value: "USD", label: "$ USD" },
          ]}
        />
        {onPaidPlan && (
          <Button
            variant="default"
            small
            disabled={busy !== null}
            onClick={() => void openPortal()}
            className="ml-auto"
          >
            {busy === "__portal" ? t.opening : t.manageBilling}
          </Button>
        )}
      </div>

      {plans.isLoading ? (
        <Card className="!p-6 text-center font-mono text-[12px] text-muted">
          {t.loadingPlans}
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {(plans.data ?? [])
            .slice()
            .sort((a, b) => a.display_order - b.display_order)
            .map((p) => {
              const isCurrent = p.slug === plan.slug;
              const isEnterprise = p.slug === "enterprise";
              const isSelfServe = SELF_SERVE.has(p.slug);

              return (
                <Card
                  key={p.slug}
                  className={clsx("!p-4 flex flex-col")}
                  style={isCurrent ? { borderColor: "rgb(var(--color-accent))" } : undefined}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-display text-[18px] font-semibold text-content">
                      {p.name}
                    </span>
                    {isCurrent && (
                      <span
                        className="px-[6px] py-[2px] rounded-[3px] text-[9px] font-bold uppercase"
                        style={{
                          background: "rgb(var(--color-accent) / 0.133)",
                          border: "1px solid rgb(var(--color-accent) / 0.533)",
                          color: "rgb(var(--color-accent))",
                        }}
                      >
                        {t.current}
                      </span>
                    )}
                  </div>

                  <div className="font-mono text-[11px] text-muted leading-[1.5] mb-3 min-h-[32px]">
                    {p.description}
                  </div>

                  <div className="mb-3">
                    {p.slug === "free" ? (
                      <span className="font-display text-[20px] font-bold text-content">
                        {t.free}
                      </span>
                    ) : isEnterprise ? (
                      <span className="font-display text-[16px] font-semibold text-content">
                        {t.custom}
                      </span>
                    ) : (
                      <>
                        <span className="font-display text-[20px] font-bold text-content">
                          {p.prices[currency][cadence].display}
                        </span>
                        <span className="font-mono text-[10px] text-muted">
                          {cadence === "annual" ? t.perYr : t.perMo}
                        </span>
                        {cadence === "annual" && (
                          <div className="font-mono text-[10px] text-success mt-[2px]">
                            {t.twoMonthsFree}
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  <ul className="flex-1 font-mono text-[11px] text-muted space-y-1 mb-4">
                    <li>{fmtLimit(p.max_scans_per_month, t.unlimited)} {t.scansLabel}</li>
                    <li>{fmtLimit(p.max_engagements, t.unlimited)} {t.engagementsLabel}</li>
                    <li>{fmtLimit(p.max_users, t.unlimited)} {t.membersLabel}</li>
                    {FEATURE_FLAGS.map((flag) =>
                      p.features[flag] ? (
                        <li key={flag} className="text-success">
                          ✓ {t.features[flag]}
                        </li>
                      ) : null,
                    )}
                  </ul>

                  {isCurrent ? (
                    <Button variant="default" full disabled>
                      {t.yourPlan}
                    </Button>
                  ) : isEnterprise ? (
                    <a
                      href="mailto:sales@truehacking.ai?subject=Enterprise%20plan"
                      className="no-underline"
                    >
                      <Button variant="default" full>
                        {t.contactSales}
                      </Button>
                    </a>
                  ) : isSelfServe ? (
                    <Button
                      variant="primary"
                      full
                      disabled={busy !== null}
                      onClick={() => void startCheckout(p.slug)}
                    >
                      {busy === p.slug ? t.starting : fmt(t.choose, { name: p.name })}
                    </Button>
                  ) : (
                    <Button variant="default" full disabled>
                      —
                    </Button>
                  )}
                </Card>
              );
            })}
        </div>
      )}

      <div className="font-mono text-[10px] text-dim leading-[1.6] mt-4 text-center">
        {t.footnote}
      </div>
    </div>
  );
}

// Small segmented toggle, local to this page.
function Toggle<T extends string>({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: T;
  onChange: (v: T) => void;
  options: Array<{ value: T; label: string }>;
}) {
  return (
    <div className="flex items-center gap-2">
      <span className="font-mono text-[10px] text-muted uppercase tracking-[0.08em]">
        {label}
      </span>
      <div className="inline-flex rounded border border-stroke overflow-hidden">
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange(o.value)}
            className={clsx(
              "px-3 py-[5px] font-mono text-[11px] cursor-pointer border-none",
              value === o.value ? "text-white" : "bg-transparent text-muted",
            )}
            style={value === o.value ? { background: "rgb(var(--color-accent))" } : undefined}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}
